import { useState, useMemo } from "react";
import { Settings as SettingsIcon, Save, ExternalLink, FlaskConical, Globe, Search, Plus, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface Setting {
  id: string;
  key: string;
  value: unknown;
  description: string | null;
}

export default function Settings() {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('settings').select('*');
      if (error) throw error;
      return data as Setting[];
    },
  });

  const [formData, setFormData] = useState<Record<string, string>>({});

  const getSetting = (key: string) => {
    const setting = settings?.find((s) => s.key === key);
    if (formData[key] !== undefined) return formData[key];
    if (!setting) return '';
    const value = setting.value;
    if (typeof value === 'boolean') return String(value);
    return typeof value === 'string' ? value : JSON.stringify(value);
  };

  const getSettingBool = (key: string): boolean => {
    if (formData[key] !== undefined) return formData[key] === 'true';
    const setting = settings?.find((s) => s.key === key);
    if (!setting) return false;
    return setting.value === true || setting.value === 'true';
  };

  const handleToggle = async (key: string, value: boolean) => {
    try {
      const { error } = await supabase
        .from('settings')
        .upsert({ key, value, description: key === 'test_mode' ? 'Тестовый режим: отключает проверку баланса хитов' : null }, { onConflict: 'key' });
      
      if (error) throw error;
      
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success(value ? "Тестовый режим включён" : "Тестовый режим выключен");
    } catch (error) {
      toast.error("Ошибка при сохранении");
    }
  };

  const handleChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const [key, value] of Object.entries(formData)) {
        let parsedValue: unknown = value;
        try {
          parsedValue = JSON.parse(value);
        } catch {
          // Keep as string
        }
        
        const { error } = await supabase
          .from('settings')
          .upsert({ key, value: parsedValue as string }, { onConflict: 'key' });
        
        if (error) throw error;
      }
      
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setFormData({});
      toast.success("Настройки сохранены");
    } catch (error) {
      toast.error("Ошибка при сохранении");
    } finally {
      setSaving(false);
    }
  };

  const telegramUrl = String(getSetting('telegram_bot_url')).replace(/\"/g, '');

  return (
    <AdminLayout title="Настройки" description="Конфигурация системы">
      <div className="space-y-6">
        {/* Test Mode */}
        <Card className="bg-card border-border border-yellow-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-yellow-500" />
              Тестовый режим
            </CardTitle>
            <CardDescription>Режим для тестирования без списания хитов</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="test_mode">Включить тестовый режим</Label>
                <p className="text-xs text-muted-foreground">
                  Генерации будут бесплатными, баланс не уменьшается
                </p>
              </div>
              <Switch
                id="test_mode"
                checked={getSettingBool('test_mode')}
                onCheckedChange={(checked) => handleToggle('test_mode', checked)}
              />
            </div>
            {getSettingBool('test_mode') && (
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-500 font-medium">
                  🧪 Тестовый режим активен — хиты не списываются!
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SEO Settings */}
        <Card className="bg-card border-border border-blue-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-500" />
              SEO настройки
            </CardTitle>
            <CardDescription>Оптимизация для поисковых систем (Google, Яндекс)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="seo_title">Заголовок страницы (Title)</Label>
              <Input
                id="seo_title"
                value={String(getSetting('seo_title')).replace(/\"/g, '')}
                onChange={(e) => handleChange('seo_title', `"${e.target.value}"`)}
                placeholder="Песня на заказ за 1 минуту — Хит Шеф | Создать песню в подарок"
              />
              <p className="text-xs text-muted-foreground">
                До 60 символов. Отображается во вкладке браузера и в поиске
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="seo_description">Описание (Meta Description)</Label>
              <Textarea
                id="seo_description"
                value={String(getSetting('seo_description')).replace(/\"/g, '')}
                onChange={(e) => handleChange('seo_description', `"${e.target.value}"`)}
                placeholder="Закажите персональную песню в подарок на день рождения, свадьбу или любой праздник. Создайте уникальную песню на заказ с помощью AI за 1 минуту! Музыка на заказ от Хит Шеф."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                До 160 символов. Отображается в результатах поиска
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="seo_keywords">Ключевые слова</Label>
              <Input
                id="seo_keywords"
                value={String(getSetting('seo_keywords')).replace(/\"/g, '')}
                onChange={(e) => handleChange('seo_keywords', `"${e.target.value}"`)}
                placeholder="песня на заказ, создать песню, песня в подарок, музыка на заказ, заказать песню, персональная песня, песня на день рождения, песня на свадьбу"
              />
              <p className="text-xs text-muted-foreground">
                Через запятую. Помогает поисковикам понять тематику сайта
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="seo_og_image">OG Image URL (для соцсетей)</Label>
              <Input
                id="seo_og_image"
                value={String(getSetting('seo_og_image')).replace(/\"/g, '')}
                onChange={(e) => handleChange('seo_og_image', `"${e.target.value}"`)}
                placeholder="https://hitchef.ru/og-image.png"
              />
              <p className="text-xs text-muted-foreground">
                Картинка 1200x630px для превью при шаринге в соцсетях
              </p>
            </div>

            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg space-y-2">
              <p className="text-sm text-blue-400">
                💡 <strong>Текущие значения в index.html:</strong>
              </p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                <li><strong>Title:</strong> Песня на заказ за 1 минуту — Хит Шеф | Создать песню в подарок</li>
                <li><strong>Description:</strong> Закажите персональную песню в подарок на день рождения, свадьбу или любой праздник...</li>
                <li><strong>Keywords:</strong> песня на заказ, создать песню, песня в подарок, музыка на заказ, заказать песню, персональная песня...</li>
                <li><strong>OG Image:</strong> https://hitchef.ru/og-image.png</li>
              </ul>
              <p className="text-xs text-yellow-400 mt-2">
                ⚠️ Изменения здесь сохраняются в БД. Для обновления index.html нужна пересборка проекта.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-primary" />
              Telegram бот
            </CardTitle>
            <CardDescription>Настройки Telegram бота</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="telegram_bot_url">URL бота</Label>
              <div className="flex gap-2">
                <Input
                  id="telegram_bot_url"
                  value={String(getSetting('telegram_bot_url')).replace(/\"/g, '')}
                  onChange={(e) => handleChange('telegram_bot_url', `"${e.target.value}"`)}
                  placeholder="https://t.me/your_bot"
                />
                {telegramUrl && (
                  <Button variant="outline" size="icon" asChild>
                    <a href={telegramUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Admin Telegram Username */}
        <Card className="bg-card border-border border-cyan-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-cyan-500" />
              Telegram администратора
            </CardTitle>
            <CardDescription>Используется для тестовой отправки рассылок</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin_telegram_username">Username</Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">@</span>
                <Input
                  id="admin_telegram_username"
                  value={String(getSetting('admin_telegram_username')).replace(/\"/g, '')}
                  onChange={(e) => handleChange('admin_telegram_username', `"${e.target.value.replace('@', '')}"`)}
                  placeholder="DigitalAndy"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Ваш Telegram username (без @). Должен совпадать с тем, что видит бот
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Free Generation Limit */}
        <Card className="bg-card border-border border-orange-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-orange-500" />
              Лимит бесплатных генераций
            </CardTitle>
            <CardDescription>Количество бесплатных генераций (текст + демо) для новых пользователей</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="free_generation_limit">Количество бесплатных генераций</Label>
              <Input
                id="free_generation_limit"
                type="number"
                min="0"
                value={String(getSetting('free_generation_limit')).replace(/\"/g, '')}
                onChange={(e) => handleChange('free_generation_limit', e.target.value)}
                placeholder="3"
              />
              <p className="text-xs text-muted-foreground">
                После этого количества пользователь должен купить хиты для продолжения
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Full Track Price */}
        <Card className="bg-card border-border border-green-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-green-500" />
              Цена полной версии
            </CardTitle>
            <CardDescription>Стоимость покупки полного трека после демо</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_track_price">Цена (₽)</Label>
              <Input
                id="full_track_price"
                type="number"
                value={String(getSetting('full_track_price')).replace(/\"/g, '')}
                onChange={(e) => handleChange('full_track_price', e.target.value)}
                placeholder="449"
              />
              <p className="text-xs text-muted-foreground">
                Пользователь получает бесплатно 20сек демо, затем платит эту сумму за полный трек
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Hit Packages */}
        <Card className="bg-card border-border border-purple-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-purple-500" />
              Пакеты хитов
            </CardTitle>
            <CardDescription>Цены и названия пакетов хитов для покупки в боте</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(() => {
              const raw = getSetting('hit_packages');
              let packages: Record<string, any> = {};
              try { packages = typeof raw === 'string' ? JSON.parse(raw) : {}; } catch {}
              const sortedKeys = Object.keys(packages).sort((a, b) => Number(a) - Number(b));
              
              return (
                <>
                  {sortedKeys.map((qty) => {
                    const pkg = packages[qty] || {};
                    return (
                      <div key={qty} className="grid grid-cols-[80px_1fr_1fr_1fr_40px] gap-2 items-end">
                        <div className="space-y-1">
                          <Label className="text-xs">Кол-во</Label>
                          <Input value={qty} disabled className="font-mono" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Цена (₽)</Label>
                          <Input
                            type="number"
                            value={pkg.price || ''}
                            onChange={(e) => {
                              const updated = { ...packages, [qty]: { ...pkg, price: Number(e.target.value) } };
                              handleChange('hit_packages', JSON.stringify(updated));
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Название</Label>
                          <Input
                            value={pkg.name || ''}
                            onChange={(e) => {
                              const updated = { ...packages, [qty]: { ...pkg, name: e.target.value } };
                              handleChange('hit_packages', JSON.stringify(updated));
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Экономия</Label>
                          <Input
                            value={pkg.savings || ''}
                            placeholder="экономия 30%"
                            onChange={(e) => {
                              const updated = { ...packages, [qty]: { ...pkg, savings: e.target.value || null } };
                              handleChange('hit_packages', JSON.stringify(updated));
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs opacity-0">X</Label>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-10 w-10 text-destructive hover:text-destructive"
                            onClick={() => {
                              const updated = { ...packages };
                              delete updated[qty];
                              handleChange('hit_packages', JSON.stringify(updated));
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  
                  <div className="flex gap-2 items-end">
                    <div className="space-y-1">
                      <Label className="text-xs">Новый пакет (кол-во хитов)</Label>
                      <div className="flex gap-2">
                        <Input
                          id="new-package-qty"
                          type="number"
                          min="1"
                          placeholder="Например: 3"
                          className="w-32 font-mono"
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            const input = document.getElementById('new-package-qty') as HTMLInputElement;
                            const newQty = input?.value?.trim();
                            if (!newQty || Number(newQty) < 1) return;
                            if (packages[newQty]) {
                              toast.error(`Пакет на ${newQty} хитов уже существует`);
                              return;
                            }
                            const updated = {
                              ...packages,
                              [newQty]: { price: 0, name: `${newQty} ${Number(newQty) === 1 ? 'Хит' : 'Хитов'}`, emoji: '💎', savings: null },
                            };
                            handleChange('hit_packages', JSON.stringify(updated));
                            input.value = '';
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Добавить
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
            <p className="text-xs text-muted-foreground">
              Изменения применяются сразу после сохранения — бот берёт цены из базы
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-primary" />
              Пакеты WOW-хитов
            </CardTitle>
            <CardDescription>Цены на пакеты (устаревшая модель, для существующих покупок)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prices">Цены (JSON)</Label>
              <Input
                id="prices"
                value={String(getSetting('prices'))}
                onChange={(e) => handleChange('prices', e.target.value)}
                placeholder='{"1": 199, "3": 399, "5": 599}'
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Формат: {`{ "количество": цена_в_рублях }`}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Counters */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-primary" />
              Счётчики
            </CardTitle>
            <CardDescription>Общие счётчики для отображения</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="total_hits_generated">Всего хитов сгенерировано</Label>
              <Input
                id="total_hits_generated"
                value={String(getSetting('total_hits_generated')).replace(/\"/g, '')}
                onChange={(e) => handleChange('total_hits_generated', `"${e.target.value}"`)}
                placeholder="12847"
              />
              <p className="text-xs text-muted-foreground">
                Отображается на главной странице
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Save button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || Object.keys(formData).length === 0}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Сохранение...' : 'Сохранить изменения'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
